import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { Extras, Price } from 'shared-types/Common';
import { ExtrasTemplate } from 'voyager';
import type { ExtrasEditResult } from 'voyager';
import { RoomRateFormValues } from '../../RoomRateForm.schema';
import { RoomRateFormStepNames } from '../constants';
import { priceWithExtras } from '../ExtrasStep/utils';
import { GuestDetailsSelectedEntity } from '../GuestDetailsStepV2/GuestDetailsStepV2.types';
import { findSelectedEntity } from '../selectedEntity.helper';
import { ExtrasStepV2Props } from './ExtrasStepV2.types';
import {
  ExtraFormEntry,
  ExtraOptionSelectionState,
  buildExtrasBasketPayload,
  buildExtrasGuests,
  buildExtrasItems,
} from './utils';

const bookingRoutesMap = {
  room: Routes.ROOM_BOOKING,
  rate: Routes.RATE_BOOKING,
};

export const ExtrasStepV2 = ({
  metaEntity,
  entity,
  availableExtras,
  addToBasket,
  goToNextStep,
  goToPreviousStep,
  isFetching,
  language,
}: ExtrasStepV2Props) => {
  const { t } = useTranslations();
  const router = useRouter();
  const form = useFormContext<RoomRateFormValues>();
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, isSubmitting },
  } = form;

  const guestValues = watch('guests') ?? [];
  const extrasValues = (watch('extras') as (ExtraFormEntry | undefined)[] | undefined) ?? [];

  const optionSelections = useMemo(() => {
    const map: Record<number, ExtraOptionSelectionState | undefined> = {};
    extrasValues.forEach((entry, index) => {
      if (entry?.optionSelections) {
        map[index] = entry.optionSelections;
      }
    });
    return map;
  }, [extrasValues]);

  const guestSelections = useMemo(() => {
    const map: Record<number, string[] | undefined> = {};
    extrasValues.forEach((entry, index) => {
      if (entry?.guestSelections) {
        map[index] = entry.guestSelections;
      }
    });
    return map;
  }, [extrasValues]);

  const areGuestsLoaded = guestValues.some(
    (guest) => guest.firstName || guest.lastName || guest.title
  );

  useEffect(() => {
    if (areGuestsLoaded) {
      return;
    }
    router.push({
      pathname: bookingRoutesMap[metaEntity.type],
      query: {
        id: metaEntity.id,
        bookingStep: RoomRateFormStepNames.GUESTS,
        [entity.type]: entity.id,
      },
    });
  }, [areGuestsLoaded, router, metaEntity.type, metaEntity.id, entity.type, entity.id]);

  const selectedEntity = findSelectedEntity(metaEntity, entity.id) as
    | GuestDetailsSelectedEntity
    | undefined;

  const extras = useMemo(() => availableExtras ?? [], [availableExtras]);

  const guests = useMemo(
    () =>
      buildExtrasGuests(guestValues, (guestNumber) =>
        t('room.guestDetails.guestTitle', { number: guestNumber })
      ),
    [guestValues, t]
  );

  const currencyCode =
    selectedEntity?.pricePerStay?.currencyCode ?? extras[0]?.price?.currencyCode ?? 'GBP';

  const items = useMemo(
    () =>
      buildExtrasItems({
        extras,
        entries: extrasValues,
        optionSelections,
        guestSelections,
        language,
        fallbackCurrencyCode: currencyCode,
      }),
    [extras, extrasValues, optionSelections, guestSelections, language, currencyCode]
  );

  const setExtraEntry = (index: number, patch: Partial<ExtraFormEntry>) => {
    const current = (form.getValues('extras') as (ExtraFormEntry | undefined)[] | undefined) ?? [];
    const updated = [...current];
    updated[index] = { ...updated[index], ...patch };
    setValue('extras', updated as RoomRateFormValues['extras'], { shouldValidate: true });
  };

  const handleAdd = (itemId: string) => {
    setExtraEntry(Number(itemId), { isSelected: true });
  };

  const handleRemove = (itemId: string) => {
    setExtraEntry(Number(itemId), { isSelected: false });
  };

  const handleConfirmGuestSelection = (itemId: string, selectedGuestIds: string[]) => {
    const index = Number(itemId);
    if (selectedGuestIds.length) {
      setExtraEntry(index, {
        isSelected: true,
        quantity: selectedGuestIds.length,
        guestSelections: selectedGuestIds,
      });
    } else {
      setExtraEntry(index, { isSelected: false, guestSelections: selectedGuestIds });
    }
  };

  const handleConfirmSelection = (itemId: string, result: ExtrasEditResult) => {
    const index = Number(itemId);
    if (result.type === 'quantity') {
      setExtraEntry(index, { isSelected: true, quantity: result.quantity });
      return;
    }
    const firstSelection = result.selections[0];
    if (!firstSelection) {
      setExtraEntry(index, { isSelected: false });
      return;
    }
    const isPerPerson = !!extras[index]?.pricingType?.startsWith('per_person');
    const quantity =
      result.applyToAll && isPerPerson && guests.length > 1
        ? guests.length
        : result.selections.length;
    setExtraEntry(index, {
      isSelected: true,
      option: Number(firstSelection.optionId),
      quantity,
      optionSelections: { selections: result.selections, applyToAll: result.applyToAll },
    });
  };

  const displayPrice = useMemo<Price | null>(
    () =>
      selectedEntity?.pricePerStay
        ? priceWithExtras(
            selectedEntity as { pricePerStay: Price },
            extras as unknown as Extras[],
            extrasValues.map((entry) => entry ?? {})
          )
        : null,
    [selectedEntity, extras, extrasValues]
  );

  const onSubmit = async (values: RoomRateFormValues) => {
    const extrasPayload = buildExtrasBasketPayload(
      extras,
      (values.extras as (ExtraFormEntry | undefined)[] | undefined) ?? [],
      optionSelections
    );
    await addToBasket({ guestDetails: values.guests, extras: extrasPayload, note: values.note });
    goToNextStep();
  };

  const weekdays = useMemo(() => moment.localeData(language).weekdaysMin(), [language]);

  const labels = useMemo(
    () => ({
      title: t('room.extrasStepTitle'),
      subtitle: t('room.extras.subtitle'),
      backAriaLabel: t('common.back'),
      add: t('room.extras.add'),
      remove: t('room.extras.remove'),
      quantity: t('room.extras.quantity'),
      editSelections: t('room.extras.editSelections'),
      noImage: t('room.extras.noImage'),
      footerTitle: metaEntity.title ?? t('room.extrasStepTitle'),
      footerTotal: t('basket.total'),
      ctaLabel: t('booking.continue'),
      modalTitle: t('room.extras.selectGuests'),
      modalConfirm: t('modalError.confirm'),
      modalClose: t('journeypicker.close'),
      modalTotal: t('basket.total'),
      dateModalTitle: t('room.extras.selectDate'),
      dateRangeModalTitle: t('room.extras.selectDateRange'),
      timeRangeModalTitle: t('room.extras.selectTimeRange'),
      dateTimeModalTitle: t('room.extras.selectDateTime'),
      dateTimeRangeModalTitle: t('room.extras.selectDateTimeRange'),
      quantityModalTitle: t('room.extras.selectQuantity'),
      applyToAll: t('room.extras.applyToAll'),
      perUnit: t('room.extras.perUnit'),
      perGuest: t('room.extras.perGuest'),
      timesLabel: t('room.extras.times'),
      previousWeek: t('room.extras.previousWeek'),
      nextWeek: t('room.extras.nextWeek'),
      progressLabel: t('aria.bookingProgress'),
      weekdays,
    }),
    [t, metaEntity.title, weekdays]
  );

  return (
    <ExtrasTemplate
      items={items}
      guests={guests.length > 1 ? guests : undefined}
      currentStep={2}
      totalSteps={2}
      totalAmount={displayPrice?.amount ?? 0}
      currencyCode={currencyCode}
      locale={language}
      onBack={goToPreviousStep}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onConfirmGuestSelection={handleConfirmGuestSelection}
      onConfirmSelection={handleConfirmSelection}
      onCtaClick={handleSubmit(onSubmit)}
      ctaDisabled={!isValid || isSubmitting}
      isLoading={isFetching}
      labels={labels}
    />
  );
};
